import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  completeWhatsAppEmbeddedSignup,
  getWhatsAppEmbeddedSignupConfig,
  getWhatsAppStatus
} from '../../../../../../../../services/whatsappIntegrationService';
import { launchWhatsAppEmbeddedSignup } from '../../../../../../../../utils/whatsappEmbeddedSignup';
import { useToast } from '../../../../../../../components/Toats';

interface WhatsAppConnectionProps {
  companyId: string;
}

export function WhatsAppConnection({ companyId }: WhatsAppConnectionProps) {
  const { toggleToast } = useToast();
  const [connecting, setConnecting] = useState(false);

  const { data: status, isLoading, error, refetch } = useQuery(
    ['whatsappStatus', companyId],
    () => getWhatsAppStatus(companyId),
    {
      enabled: !!companyId,
      refetchInterval: 5000
    }
  );

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'verified':
        return 'success';
      case 'connected':
        return 'info';
      case 'pending':
        return 'warning';
      case 'disconnected':
        return 'danger';
      default:
        return 'danger';
    }
  };

  const getStatusText = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'verified':
        return 'Verificado';
      case 'connected':
        return 'Conectado';
      case 'pending':
        return 'Aguardando verificação';
      case 'failed':
        return 'Falha na conexão';
      default:
        return 'Desconectado';
    }
  };

  const handleConnectWhatsApp = async () => {
    if (!companyId) {
      toggleToast('Empresa não encontrada para conectar o WhatsApp', 'error');
      return;
    }

    setConnecting(true);

    try {
      const config = await getWhatsAppEmbeddedSignupConfig();
      const signup = await launchWhatsAppEmbeddedSignup(config);
      await completeWhatsAppEmbeddedSignup({
        companyId,
        code: signup.code,
        waba_id: signup.wabaId,
        phone_number_id: signup.phoneNumberId,
        coexistence: signup.coexistence
      });

      const connectedStatus = await refetch();
      if (!connectedStatus.data?.connected) {
        throw new Error('A Meta autorizou, mas a conexão não foi gravada. Tente conectar novamente.');
      }
      toggleToast('WhatsApp conectado com sucesso', 'success');
    } catch (connectError: any) {
      const apiMessage =
        connectError?.response?.data?.message
        || connectError?.message
        || 'Erro ao conectar WhatsApp';
      toggleToast(apiMessage, 'error');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
        <div style={{flex: '1'}}>
            {status && (
                <div className="row">
                    <div className="col-md-6">
                        <div className="card p-0 bg-light">
                            <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-3">
                                <div>
                                    <div className="d-flex align-items-center">
                                    <div className={`badge badge-${getStatusColor(status.status)} me-2`}>
                                        {getStatusText(status.status)}
                                    </div>
                                    </div>
                                    {status.waba_id && (
                                        <p className="mb-0 mt-2">
                                            <span className='fw-semibold text-muted'>WABA ID: </span><span>{status.waba_id}</span>
                                        </p>
                                    )}
                                    {status.phone_number && (
                                        <p className="mb-0">
                                            <span className='fw-semibold text-muted'>Número: </span><span>{status.phone_number}</span>
                                        </p>
                                    )}
                                </div>

                                <button
                                    onClick={() => refetch()}
                                    className="btn btn-sm btn-primary"
                                    disabled={isLoading}
                                    >
                                {isLoading ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    'Atualizar'
                                )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!status && !isLoading && !error && (
                <div className="alert alert-info">
                    Status não disponível. Conecte o WhatsApp primeiro.
                </div>
            )}
        </div>

        <div>
          <button
            onClick={handleConnectWhatsApp}
            className='btn btn-primary'
            disabled={connecting}
          >
            {!connecting && 'Conectar WhatsApp'}
            {connecting && (
              <span className='indicator-progress'>
                Aguarde...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
